<template>
  <header
    class="relative top-0 flex flex-col justify-center w-full min-w-full px-4 py-2 pb-2 md:px-0 max-w-desktop min-h-14 z-60"
    ref="header"
  >
    <!-- Internal app notifications -->
    <app-notifications v-if="viewport.width < $screen.md" />

    <div v-if="!replaceHeader" class="flex items-center justify-between min-h-10">
      <div class="flex items-center justify-center gap-2" v-if="$slots['left-side']">
        <slot name="left-side"></slot>
      </div>
      <div
        v-else-if="!$slots['left-side'] && isMobile && (menuStyle === 'ontop' || menuStyle === 'page' || menuStyle === 'large')"
        class="relative mr-3 cursor-pointer"
        @click="menuStyle === 'ontop' ? toggleMenuTop() : $refs.modal.show()"
      >
        <ph-list :size="24" :color="$helpers.getCorrectColor(themeColorHEX)" />
        <div
          class="absolute flex items-center justify-center px-1 font-semibold text-white rounded-full -top-1 -right-2 bg-red text-12 min-w-5 min-h-5"
          v-if="prioritizedBubble !== 0 && prioritizedBubbleType === 'number'"
        >
          {{ prioritizedBubble > 99 ? '99+' : prioritizedBubble }}
        </div>
        <div
          class="absolute rounded-full -top-1 -right-1 bg-red min-w-2 min-h-2"
          v-else-if="prioritizedBubble !== 0 && prioritizedBubbleType === 'dot'"
        />
      </div>
      <div class="flex items-center gap-1 truncate">
        <slot name="title-left-side"></slot>
        <t-heading tag="h3" class="font-semibold truncate" @click.native="$emit('clickTitle')" :title="title" :branded="true">{{
          title
        }}</t-heading>
        <slot name="title-right-side"></slot>
      </div>
      <div
        class="flex items-center justify-center gap-2"
        :class="{'ml-3': !$slots['left-side'] && isMobile && (menuStyle === 'ontop' || menuStyle === 'page' || menuStyle === 'large')}"
        v-if="$slots['right-side']"
      >
        <slot name="right-side"></slot>
      </div>
    </div>
    <div v-else class="flex items-center justify-between gap-2">
      <slot name="header-place"></slot>
      <div
        class="p-2 cursor-pointer"
        @click="
          replaceHeader = false
          $emit('closeHeaderContent', false)
        "
      >
        <ph-x :size="24" class="text-black" />
      </div>
    </div>
    <div
      ref="headerFooter"
      v-show="$slots['header-footer']"
      class="absolute flex flex-col items-start justify-center invisible gap-2 overflow-hidden"
    >
      <t-loader-skeleton v-if="!footerLoaded" :w="'80px'" :h="'40px'" :skeletons="3" :direction="'row'" />
      <slot name="header-footer"></slot>
    </div>
    <div ref="headerMenu" class="absolute inset-x-0 h-0 overflow-hidden duration-200 top-14 z-max" v-if="menuStyle === 'ontop'">
      <div
        class="absolute inset-0 hidden duration-200 opacity-0 -mr-px2"
        ref="overlay"
        @click="toggleMenuTop()"
        :class="{'bg-black/70': theme === 'light', 'bg-concrete/40': theme === 'dark'}"
      ></div>
      <div
        class="absolute top-0 left-0 scrollbar-hidden flex flex-col max-h-full max-w-[19rem] min-w-[19rem] px-4 pt-4 gap-4 pb-12 overflow-y-scroll bg-concrete rounded-br-xl"
        :style="{bottom: $helpers.safeZone('bottom')}"
      >
        <t-list-item
          v-for="item in userMenu.filter(el => el.type !== 'installIos' && el.type !== 'installAndroid')"
          :ref="`sidebar.${item.app_module_id}`"
          :key="`sidebar.${item.app_module_id}`"
          :to="`/${item.type}/${item.app_module_id}`"
          :iconSrc="item.icon"
          :bubble="item.bubble.length"
          :bubbleType="$helpers.getBubbleTypeFromMenuType(item.type)"
          :active="$route.fullPath.split('/')[2] == item.app_module_id"
          :text="item.name"
          @click.native="goToMenu(item)"
        />
        <t-list-item
          v-if="userHideAndroidAlert && installAndroid"
          :iconSrc="installAndroid.icon"
          :bubble="installAndroid.bubble.length"
          :bubbleType="$helpers.getBubbleTypeFromMenuType(installAndroid.type)"
          :active="$route.fullPath.split('/')[2] == installAndroid.app_module_id"
          :text="installAndroid.name"
          @click.native="
            toggleMenuTop()
            handleInstallPrompt({platform: 'android', show: true})
          "
        />
        <t-list-item
          v-else-if="userHideIosAlert && installIos"
          :iconSrc="installIos.icon"
          :bubble="installIos.bubble.length"
          :bubbleType="$helpers.getBubbleTypeFromMenuType(installIos.type)"
          :active="$route.fullPath.split('/')[2] == installIos.app_module_id"
          :text="installIos.name"
          @click.native="
            toggleMenuTop()
            handleInstallPrompt({platform: 'ios', show: true})
          "
        />
      </div>
    </div>
    <div class="absolute bottom-0 md:left-0 md:right-0 left-4 right-4 h-px2 bg-platinum"></div>

    <!-- FULLSCREEN MENU MODAL -->
    <t-modal
      ref="modal"
      v-if="menuStyle === 'page' || menuStyle === 'large'"
      fullscreen
      :actionBtns="false"
      :background="theme === 'light' ? 'bg-concrete' : 'bg-white'"
    >
      <img :src="appLogo" class="max-w-[9rem] max-h-[9rem] mx-auto mb-4 mt-20 rounded-lg" />
      <div class="flex flex-col gap-4 p-4 mb-4 overflow-y-scroll scrollbar-hidden" v-if="menuStyle === 'page'">
        <t-button
          v-for="item in userMenu.filter(el => el.type !== 'installIos' && el.type !== 'installAndroid')"
          :key="`page_menu_${item.app_module_id}`"
          class="relative"
          @click.native="goToMenu(item)"
        >
          <div class="relative flex items-center w-full gap-2">
            <div class="absolute left-4 max-w-6 max-h-6">
              <div
                class="absolute flex items-center justify-center px-1 font-semibold text-white rounded-full -right-2 bg-red text-12 min-w-5 min-h-5"
                v-if="item.bubble.length !== 0 && $helpers.getBubbleTypeFromMenuType(item.type) === 'number'"
              >
                {{ item.bubble.length > 99 ? '99+' : item.bubble.length }}
              </div>
              <div
                class="absolute rounded-full -right-1 bg-red min-w-2 min-h-2"
                v-else-if="item.bubble.length !== 0 && $helpers.getBubbleTypeFromMenuType(item.type) === 'dot'"
              />
              <div
                v-html="item.icon"
                class="w-full h-full"
                :style="{color: $route.fullPath.split('/')[2] == item.app_module_id && !first ? themeColorHEX : ''}"
              ></div>
            </div>
            <t-text
              class="w-full mr-3 font-semibold text-left truncate ml-13"
              :branded="$route.fullPath.split('/')[2] == item.app_module_id && !first"
              >{{ item.name }}</t-text
            >
          </div>
        </t-button>
        <t-button
          v-if="userHideAndroidAlert && installAndroid"
          class="relative"
          @click.native="
            $refs.modal.hide()
            handleInstallPrompt({platform: 'android', show: true})
          "
        >
          <div class="relative flex items-center w-full gap-2">
            <div class="absolute left-4 max-w-6 max-h-6">
              <div v-html="installAndroid.icon" class="w-full h-full min-w-6"></div>
            </div>
            <t-text class="w-full mr-3 font-semibold truncate ml-13">{{ installAndroid.name }}</t-text>
          </div>
        </t-button>
        <t-button
          v-else-if="userHideIosAlert && installIos"
          class="relative"
          @click.native="
            $refs.modal.hide()
            handleInstallPrompt({platform: 'ios', show: true})
          "
        >
          <div class="relative flex items-center w-full gap-2">
            <div class="absolute left-4 max-w-6 max-h-6">
              <div v-html="installIos.icon" class="w-full h-full min-w-6"></div>
            </div>
            <t-text class="w-full mr-3 font-semibold truncate ml-13">{{ installIos.name }}</t-text>
          </div>
        </t-button>
      </div>
      <div class="flex flex-wrap gap-4 p-4 mb-4 overflow-y-scroll scrollbar-hidden" v-if="menuStyle === 'large'">
        <div
          v-for="item in userMenu.filter(el => el.type !== 'installIos' && el.type !== 'installAndroid')"
          :key="`page_menu_${item.app_module_id}`"
          @click="goToMenu(item)"
          class="duration-200 rounded-lg cursor-pointer"
          :class="{
            'hover:bg-white/50 bg-white': theme === 'light',
            'hover:bg-concrete/50 bg-concrete': theme === 'dark'
          }"
          style="width: calc(50% - 8px)"
        >
          <div class="relative flex flex-col items-center justify-center w-full gap-4 p-4 text-black">
            <div class="relative max-w-12 max-h-12">
              <div
                class="absolute flex items-center justify-center px-1 font-semibold text-white rounded-full -right-2 bg-red text-12 min-w-5 min-h-5"
                v-if="item.bubble.length !== 0 && $helpers.getBubbleTypeFromMenuType(item.type) === 'number'"
              >
                {{ item.bubble.length > 99 ? '99+' : item.bubble.length }}
              </div>
              <div
                class="absolute rounded-full -right-1 bg-red min-w-2 min-h-2"
                v-else-if="item.bubble.length !== 0 && $helpers.getBubbleTypeFromMenuType(item.type) === 'dot'"
              />
              <div
                v-html="item.icon"
                class="w-full h-full max-w-9 max-h-9"
                :style="{color: $route.fullPath.split('/')[2] == item.app_module_id && !first ? themeColorHEX : ''}"
              ></div>
            </div>
            <t-text class="w-full text-center truncate" :branded="$route.fullPath.split('/')[2] == item.app_module_id && !first">{{
              item.name
            }}</t-text>
          </div>
        </div>
        <div
          v-if="userHideAndroidAlert && installAndroid"
          @click="
            $refs.modal.hide()
            handleInstallPrompt({platform: 'android', show: true})
          "
          class="duration-200 rounded-lg cursor-pointer"
          :class="{
            'hover:bg-white/50 bg-white': theme === 'light',
            'hover:bg-concrete/50 bg-concrete': theme === 'dark'
          }"
          style="width: calc(50% - 8px)"
        >
          <div class="relative flex flex-col items-center justify-center w-full gap-4 p-4 text-black">
            <div class="relative max-w-12 max-h-12">
              <div v-html="installAndroid.icon" class="w-full h-full"></div>
            </div>
            <t-text class="w-full font-semibold text-center truncate">{{ installAndroid.name }}</t-text>
          </div>
        </div>
        <div
          v-else-if="userHideIosAlert && installIos"
          @click="
            $refs.modal.hide()
            handleInstallPrompt({platform: 'ios', show: true})
          "
          class="duration-200 rounded-lg cursor-pointer"
          :class="{
            'hover:bg-white/50 bg-white': theme === 'light',
            'hover:bg-concrete/50 bg-concrete': theme === 'dark'
          }"
          style="width: calc(50% - 8px)"
        >
          <div class="relative flex flex-col items-center justify-center w-full gap-4 p-4 text-black">
            <div class="relative max-w-12 max-h-12">
              <div v-html="installIos.icon" class="w-full h-full"></div>
            </div>
            <t-text class="w-full font-semibold text-center truncate">{{ installIos.name }}</t-text>
          </div>
        </div>
      </div>
      <div
        class="fixed left-0 flex items-center justify-start w-full px-4"
        :style="{top: `calc(${$helpers.safeZone('top')} + 16px`}"
        v-if="!first"
      >
        <ph-x class="cursor-pointer" @click.native="$refs.modal.hide()" :size="24" :color="$helpers.getCorrectColor(themeColorHEX)" />
      </div>
    </t-modal>
    <!-- MENU MODAL -->
  </header>
</template>

<script>
  import AppNotifications from '@/components/AppNotifications.vue'
  import {mapGetters} from 'vuex'
  import {mapMutations} from 'vuex'
  import handleViewport from '@/utils/mixins/handleViewport'
  export default {
    name: 't-header',
    props: {
      title: {
        type: String,
        default: ''
      }
    },
    components: {
      AppNotifications
    },
    mixins: [handleViewport],
    data() {
      return {
        replaceHeader: false,
        footerOpened: false,
        footerHeight: 0,
        headerBaseHeight: 0,
        footerLoaded: false,
        menuOpened: false,
        first: false
      }
    },
    computed: {
      ...mapGetters('layout', ['Header']),
      ...mapGetters(['themeColorHEX', 'menuStyle', 'theme', 'appLogo']),
      ...mapGetters('user', ['userMenu', 'userHideIosAlert', 'userHideAndroidAlert', 'userFirstModule', 'userProfileCompleted']),
      prioritizedBubble() {
        const prioritizedBubble = this.$helpers.getPrioritizedBubbleFromMenuEntities(this.userMenu)
        const bubble = this.userMenu.find(el => el.type === prioritizedBubble)
        if (bubble) return bubble.bubble.length
        else return 0
      },
      prioritizedBubbleType() {
        const prioritizedBubble = this.$helpers.getPrioritizedBubbleFromMenuEntities(this.userMenu)
        const bubble = this.userMenu.find(el => el.type === prioritizedBubble)
        if (bubble) return this.$helpers.getBubbleTypeFromMenuType(bubble.type)
        else return 'dot'
      },
      installIos() {
        return this.userMenu.find(el => el.type === 'installIos')
      },
      installAndroid() {
        return this.userMenu.find(el => el.type === 'installAndroid')
      }
    },
    methods: {
      ...mapMutations('user', ['handleInstallPrompt']),
      ...mapMutations('layout', ['emitContentReload']),
      switchHeader() {
        if (!this.$slots['header-place']) return
        this.replaceHeader = !this.replaceHeader
        return this.replaceHeader
      },
      openFooter() {
        this.footerOpened = true
        return this.footerOpened
      },
      closeFooter() {
        this.footerOpened = false
        return this.footerOpened
      },
      toggleFooter() {
        if (!this.$slots['header-footer']) return
        this.footerOpened = !this.footerOpened
        return this.footerOpened
      },
      toggleMenuTop() {
        if (!this.menuOpened) {
          // Open menu
          if (!this.$refs.overlay) return
          this.$refs.overlay.classList.replace('hidden', 'flex')
          setTimeout(() => {
            if (!this.$refs.overlay) return
            this.$refs.overlay.classList.replace('opacity-0', 'opacity-100')
          }, 0)
          this.$refs.headerMenu.style.height = 'calc(var(--1dvh) * 100 - 56px)'
          this.$refs.headerMenu.classList.remove('h-0')
          setTimeout(() => {
            if (!this.$refs.overlay) return
            this.$refs.headerMenu.classList.replace('overflow-hidden', 'overflow-unset')
          }, 200)
        } else {
          // Close menu
          if (!this.$refs.overlay) return
          this.$refs.overlay.classList.replace('opacity-100', 'opacity-0')
          setTimeout(() => {
            if (!this.$refs.overlay) return
            this.$refs.overlay.classList.replace('flex', 'hidden')
          }, 200)
          this.$refs.headerMenu.style.height = ''
          this.$refs.headerMenu.classList.add('h-0')
          this.$refs.headerMenu.classList.replace('overflow-unset', 'overflow-hidden')
        }
        this.menuOpened = !this.menuOpened
      },
      goToMenu(item) {
        if (this.menuStyle === 'ontop') this.toggleMenuTop()
        else if (this.menuStyle === 'page' || this.menuStyle === 'large') this.$refs.modal.hide()
        if (this.$route.path.split('/').includes(String(item.app_module_id))) {
          if (this.first) return (this.first = false)
          else return this.emitContentReload()
        }
        this.first = false
        this.$router.push({
          name: `${item.type}.index`,
          params: {
            app_module_id: item.app_module_id
          }
        })
      },
      async openMenuFirstTime() {
        if (!this.isMobile) return
        await this.componentLoaded
        if (
          (this.menuStyle === 'page' || this.menuStyle === 'large') &&
          !window.localStorage.getItem('external_reg') &&
          this.$route.params &&
          +this.$route.params.app_module_id === +this.userFirstModule.app_module_id &&
          this.userProfileCompleted
        ) {
          this.first = true
          this.$refs.modal.show()
        }
      },
      async componentLoaded() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$refs.header && this.$refs.headerFooter) {
              clearInterval(interval)
              resolve()
            }
          }, 10)
        })
      }
    },
    async created() {
      this.$eventBus.$on('showMenu', this.openMenuFirstTime)
      await this.componentLoaded()
      if (this.$slots['header-footer']) {
        this.$nextTick(() => {
          this.footerHeight = this.$refs.headerFooter.clientHeight
          this.headerBaseHeight = this.$refs.header.clientHeight
          this.$refs.headerFooter.style.height = 0
          this.$refs.headerFooter.classList.replace(['invisible', 'absolute'], 'hidden')
        })
      }
    },
    watch: {
      'footerOpened'(val) {
        const pageFooter = document.querySelector('footer')
        if (val) {
          //open
          this.$refs.headerFooter.classList.replace('hidden', ['flex', 'duration-200'])
          document.querySelector('#content').classList.add('duration-100')
          setTimeout(() => {
            this.$refs.headerFooter.style.height = `calc(${this.footerHeight}px + 8px)`
            this.$refs.headerFooter.classList.replace('pt-0', 'pt-2')
            document.querySelector('#content').style.height = `calc(100vh - ${this.headerBaseHeight}px - ${
              this.footerHeight
            }px - ${this.$helpers.safeZone('top')} - ${pageFooter ? pageFooter.clientHeight : 0}px - 8px)`
          }, 10)
        } else {
          //close
          this.$refs.headerFooter.style.height = `0px`
          this.$refs.headerFooter.classList.replace('pt-2', 'pt-0')
          setTimeout(() => {
            document.querySelector('#content').style.height = `calc(100vh - ${this.headerBaseHeight}px - ${
              pageFooter ? pageFooter.clientHeight : 0
            }px)`
          }, 100)
          setTimeout(() => {
            document.querySelector('#content').classList.remove('duration-100')
            this.$refs.headerFooter.classList.replace(['flex', 'duration-200'], 'hidden')
          }, 200)
        }
      },
      'Header._filters_loaded'(newVal) {
        this.footerLoaded = newVal
      }
    }
  }
</script>
